import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Briefcase, Plus, Loader2, Star, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { emptyJobFields } from "@/lib/jobFields";
import { JobFields } from "@/components/JobFields";

const STATUS = {
  draft: { label: "Draft — payment required", color: "#F5A623", icon: Clock },
  pending_review: { label: "Paid — in review", color: "#002EA6", icon: Clock },
  published: { label: "Published", color: "#10B981", icon: CheckCircle2 },
  expired: { label: "Expired", color: "#71717A", icon: Clock },
};

const daysLeft = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
};

export default function ClientJobs() {
  const [jobs, setJobs] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [packages, setPackages] = useState([]);
  const [payJob, setPayJob] = useState(null);
  const [f, setF] = useState({ title: "", description: "", ...emptyJobFields });
  const [pdf, setPdf] = useState(null);

  const load = () => api.get("/jobs").then(({ data }) => setJobs(data));
  useEffect(() => {
    load();
    api.get("/billing/config").then(({ data }) => setPackages(data.job_posting_packages));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: job } = await api.post("/jobs", f);
      if (pdf) {
        const fd = new FormData();
        fd.append("file", pdf);
        await api.post(`/jobs/${job.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Draft created — choose a package to publish");
      setOpen(false);
      setF({ title: "", description: "", ...emptyJobFields });
      setPdf(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pay = async (pkgId) => {
    try {
      const { data } = await api.post("/billing/checkout", {
        kind: "job_posting", package_id: pkgId, job_id: payJob.id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader overline="Reach vetted candidates" title="Job Postings">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-job-btn" className="rounded-none bg-primary hover:bg-[#09090B] transition-colors"><Plus className="h-4 w-4 mr-1" /> New posting</Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-black/10 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">New Job Posting</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <JobFields values={f} setValues={setF} pdf={pdf} setPdf={setPdf} />
              <DialogFooter><Button type="submit" data-testid="job-save-btn" disabled={saving} className="rounded-none bg-primary hover:bg-[#09090B]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create draft"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="p-8">
        {!jobs && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {jobs && jobs.length === 0 && <Empty icon={Briefcase} title="No postings yet" hint="Create a posting and publish it with a standard or featured package." />}
        {jobs && jobs.length > 0 && (
          <div className="space-y-px bg-black/10 border border-black/10">
            {jobs.map((j) => {
              const st = STATUS[j.posting_status] || STATUS.draft;
              const dl = daysLeft(j.expires_at);
              const expiringSoon = j.posting_status === "published" && dl !== null && dl <= 14;
              const isExpired = j.posting_status === "expired";
              return (
                <div key={j.id} className="bg-white p-5 flex items-center justify-between" data-testid={`cjob-${j.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{j.title}</p>
                      {j.featured && <Star className="h-3.5 w-3.5 text-primary fill-primary" />}
                    </div>
                    <p className="overline text-[0.55rem] mt-1">{j.role_category} · {j.location || "—"} · {j.interest_count} interested</p>
                    {j.posting_status === "published" && dl !== null && (
                      <p className="text-xs mt-1" style={{ color: expiringSoon ? "#F5A623" : "#71717A" }}>
                        {dl > 0 ? `Expires in ${dl} day${dl !== 1 ? "s" : ""}` : "Expiring today"}
                      </p>
                    )}
                    {isExpired && <p className="text-xs mt-1 text-muted-foreground">Renew within the grace period to relist</p>}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-xs" style={{ color: st.color }}><st.icon className="h-3.5 w-3.5" /> {st.label}</span>
                    {j.posting_status === "draft" && (
                      <Button size="sm" onClick={() => setPayJob(j)} data-testid={`pay-job-${j.id}`} className="rounded-none bg-primary hover:bg-[#09090B]">Publish & pay</Button>
                    )}
                    {(expiringSoon || isExpired) && (
                      <Button size="sm" onClick={() => setPayJob(j)} data-testid={`renew-job-${j.id}`} className="rounded-none bg-primary hover:bg-[#09090B]">Renew</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Package selection */}
      <Dialog open={!!payJob} onOpenChange={(o) => !o && setPayJob(null)}>
        <DialogContent className="bg-white border-black/10 rounded-none max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">Choose a posting package</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-px bg-black/10 border border-black/10">
            {packages.map((p) => (
              <div key={p.id} className="bg-white p-6 flex flex-col">
                <div className="flex items-center gap-2">
                  <p className="font-heading font-bold text-lg">{p.name}</p>
                  {p.id === "featured" && <Star className="h-4 w-4 text-primary fill-primary" />}
                </div>
                <p className="font-heading font-black text-3xl mt-3">${p.price}</p>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground flex-1">
                  {p.features.map((ft) => <li key={ft} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0" /> {ft}</li>)}
                </ul>
                <Button onClick={() => pay(p.id)} data-testid={`buy-${p.id}`} className="mt-5 rounded-none bg-primary hover:bg-[#09090B]">Pay ${p.price}</Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center">Secure checkout via Stripe. Your posting goes live instantly after payment.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
